// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AthleteRegistrationForm from '@/components/auth/AthleteRegistrationForm';
import { AthleteFormData } from '@/types/AthleteFormData';

interface PendingProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  sport: string;
  graduation_year?: string;
  position?: string;
  jersey_number?: string;
  metadata: any;
  phone?: string;
  city?: string;
  state?: string;
  gender?: string;
  sport_id?: string;
  position_id?: string;
  education_level?: string;
  grad_year?: number;
  college_year?: string;
}

const ProfileSetup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<PendingProfile | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid setup link');
      navigate('/');
      return;
    }
    fetchPendingProfile();
  }, [token]);

  const fetchPendingProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', token)
        .eq('status', 'pending_setup')
        .single();

      if (error || !data) {
        toast.error('Profile setup link is invalid or expired');
        navigate('/');
        return;
      }

      setProfile(data);
    } catch (error) {
      console.error('Error fetching pending profile:', error);
      toast.error('Failed to load profile setup');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationComplete = async () => {
    try {
      // The AthleteRegistrationForm handles all the registration logic
      // including auth user creation and profile setup
      // We just need to update the pending profile status to 'active'
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          status: 'active',
          completed_setup_at: new Date().toISOString(),
          metadata: {
            ...profile!.metadata,
            setup_completed: true,
            setup_completed_at: new Date().toISOString()
          }
        })
        .eq('id', profile!.id);

      if (updateError) {
        console.warn('Warning updating pending profile status:', updateError);
      }

      toast.success('Profile setup complete! Welcome to RecruitLook!');
      navigate('/athlete-dashboard');

    } catch (error: any) {
      console.error('Error completing profile setup:', error);
      toast.error(error.message || 'Failed to complete profile setup');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading profile setup...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-red-600">Profile setup link is invalid or expired</p>
              <Button onClick={() => navigate('/')} className="mt-4">
                Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 py-8">
      <div className="container max-w-4xl mx-auto px-4">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold mb-2">Complete Your Athlete Profile</h1>
          <p className="text-muted-foreground">
            Welcome to {profile.metadata?.team_name || 'your team'}! Let's finish setting up your RecruitLook profile.
          </p>
        </div>
        
        {/* Profile Information Display */}
        <div className="mb-6 p-4 bg-muted/20 rounded-lg">
          <h3 className="font-semibold mb-3">Pre-filled Information from Team Roster:</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium">Name:</span> {profile.first_name} {profile.last_name}</div>
            <div><span className="font-medium">Email:</span> {profile.email}</div>
            {profile.position && <div><span className="font-medium">Position:</span> {profile.position}</div>}
            {profile.graduation_year && <div><span className="font-medium">Graduation:</span> {profile.graduation_year}</div>}
            {profile.metadata?.team_name && <div><span className="font-medium">Team:</span> {profile.metadata.team_name}</div>}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Please complete the form below with this information plus additional required details.
          </p>
        </div>

        <AthleteRegistrationForm 
          onSuccess={handleRegistrationComplete}
          variant="premium"
          initialData={{
            name: profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : '',
            email: profile.email || '',
            phone: profile.phone || '',
            city: profile.city || '',
            state: profile.state || '',
            gender: (profile.gender === 'Men' || profile.gender === 'Women') ? profile.gender : undefined,
            sport_id: profile.sport_id || '',
            position_id: profile.position_id || '',
            education_level: (profile.education_level === 'high_school' || profile.education_level === 'college') ? profile.education_level : undefined,
            year: profile.grad_year?.toString() || profile.college_year || ''
          }}
        />
      </div>
    </div>
  );
};

export default ProfileSetup;